import { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { postContact } from "../api";

export default function ContactModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    if (!message || message.trim().length < 5) {
      setStatus({ ok: false, message: "Please write at least 5 characters in your message." });
      return;
    }
    setLoading(true);
    try {
      await postContact({ name, email, message });
      setStatus({ ok: true, message: "Thanks — we received your message and will be in touch." });
      setName("");
      setEmail("");
      setMessage("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) && detail[0]?.msg
        ? detail[0].msg
        : "Couldn't submit. Please check the form and try again.";
      setStatus({ ok: false, message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="contact-modal"
      className="fixed inset-0 z-[60] grid place-items-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[520px] rounded-2xl bg-white shadow-xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <h3 className="text-[17px] font-semibold tracking-tight text-neutral-900">
            Contact us
          </h3>
          <button
            type="button"
            onClick={onClose}
            data-testid="contact-modal-close"
            className="p-1.5 -mr-1.5 rounded-full text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="px-6 py-5 space-y-4"
          noValidate
        >
          <div>
            <label className="block text-[12px] font-medium text-neutral-700 mb-1.5">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="contact-name-input"
              className="w-full h-10 px-3.5 rounded-lg border border-neutral-300 text-[14px] focus:outline-none focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10"
            />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-neutral-700 mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="contact-email-input"
              className="w-full h-10 px-3.5 rounded-lg border border-neutral-300 text-[14px] focus:outline-none focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10"
            />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-neutral-700 mb-1.5">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              data-testid="contact-message-input"
              className="w-full px-3.5 py-2.5 rounded-lg border border-neutral-300 text-[14px] focus:outline-none focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10 resize-none"
            />
          </div>

          {status && (
            <p
              data-testid={status.ok ? "contact-status-success" : "contact-status-error"}
              className={`text-[13px] ${status.ok ? "text-emerald-700" : "text-rose-700"}`}
            >
              {status.message}
            </p>
          )}

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full h-10 px-5 text-[13px] font-medium text-neutral-700 hover:bg-neutral-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="inline-flex items-center gap-2 rounded-full bg-neutral-900 h-10 px-5 text-[13px] font-medium text-white hover:bg-neutral-800 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Send message
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
